import { EntityState, createEntityAdapter } from '@ngrx/entity';
import { createReducer, on } from '@ngrx/store';

import { PaginationMetadata } from '@core/shared/domain';
import { LoadingStatus } from '@mp/shared/domain';

import { FavoritesList, FavoritesListArticle } from '../../common';

import { FavoritesActions, FavoritesMenuActions } from './favorites.actions';

export const favoritesFeatureKey = 'mpms-core-favorites';

type FavoritesEntityState = EntityState<FavoritesList>;

export interface FavoritesListState {
  favoritesLists: FavoritesEntityState;
  favoritesListsStatus: LoadingStatus;
  favoriteListArticles: FavoritesListArticle[];
  favoriteListArticlesStatus: LoadingStatus;
  favoriteListArticlesPaginationMetadata?: PaginationMetadata;
}

export const favoritesEntityAdapter = createEntityAdapter<FavoritesList>({
  selectId: (favoritesList: FavoritesList) => favoritesList.favoritesListId,
});

export const initialState: FavoritesListState = {
  favoritesLists: favoritesEntityAdapter.getInitialState(),
  favoritesListsStatus: LoadingStatus.Loaded,
  favoriteListArticles: [],
  favoriteListArticlesStatus: LoadingStatus.Loaded,
};

export const favoritesListReducer = createReducer(
  initialState,
  on(
    FavoritesActions.fetchFavoritesLists,
    (state): FavoritesListState => ({
      ...state,
      favoritesListsStatus: LoadingStatus.Loading,
      favoritesLists: favoritesEntityAdapter.getInitialState(),
    }),
  ),

  on(
    FavoritesActions.fetchFavoritesListsSuccess,
    (state, { favoritesLists }): FavoritesListState => ({
      ...state,
      favoritesLists: favoritesEntityAdapter.setAll(favoritesLists, state.favoritesLists),
      favoritesListsStatus: LoadingStatus.Loaded,
    }),
  ),

  on(
    FavoritesActions.fetchFavoriteListArticles,
    (state): FavoritesListState => ({
      ...state,
      favoriteListArticlesStatus: LoadingStatus.Loading,
      favoriteListArticles: [],
    }),
  ),
  on(
    FavoritesActions.fetchFavoriteListArticlesSuccess,
    (state, { favoriteListArticlesPageResponse }): FavoritesListState => ({
      ...state,
      favoriteListArticlesPaginationMetadata: favoriteListArticlesPageResponse.pagination,
      favoriteListArticles: favoriteListArticlesPageResponse.data,
      favoriteListArticlesStatus: LoadingStatus.Loaded,
    }),
  ),

  on(
    FavoritesActions.createFavoritesListSuccess,
    (state, { createdList }): FavoritesListState => ({
      ...state,
      favoritesLists: favoritesEntityAdapter.addOne(createdList, state.favoritesLists),
    }),
  ),

  on(
    FavoritesActions.updateFavoritesListSuccess,
    FavoritesActions.favoritesListUpdate,
    (state, { updatedList }): FavoritesListState => ({
      ...state,
      favoritesLists: favoritesEntityAdapter.updateOne(
        { id: updatedList.favoritesListId, changes: updatedList },
        state.favoritesLists,
      ),
      favoritesListsStatus: LoadingStatus.Loaded,
    }),
  ),

  on(
    FavoritesActions.deleteFavoritesListSuccess,
    (state, { listId }): FavoritesListState => ({
      ...state,
      favoritesLists: favoritesEntityAdapter.removeOne(listId, state.favoritesLists),
      favoritesListsStatus: LoadingStatus.Loaded,
    }),
  ),

  on(
    FavoritesActions.favoriteListArticlesClear,
    (state): FavoritesListState => ({
      ...state,
      favoriteListArticles: [],
      favoriteListArticlesStatus: LoadingStatus.Loaded,
      favoriteListArticlesPaginationMetadata: undefined,
    }),
  ),

  on(
    FavoritesActions.deleteFavoriteListArticleSuccess,
    FavoritesMenuActions.deleteFavoriteListArticleSuccess,
    (state, { updatedList }): FavoritesListState => ({
      ...state,
      favoritesLists: favoritesEntityAdapter.updateOne(
        { id: updatedList.favoritesListId, changes: updatedList },
        state.favoritesLists,
      ),
    }),
  ),

  on(
    FavoritesActions.createFavoritesListError,
    FavoritesActions.updateFavoritesListError,
    FavoritesActions.deleteFavoritesListError,
    (state): FavoritesListState => ({
      ...state,
      favoritesListsStatus: LoadingStatus.Error,
    }),
  ),

  on(
    FavoritesActions.fetchFavoritesListsError,
    (state): FavoritesListState => ({
      ...state,
      favoritesListsStatus: LoadingStatus.Error,
      favoritesLists: favoritesEntityAdapter.getInitialState(),
    }),
  ),

  on(
    FavoritesActions.fetchFavoriteListArticlesError,
    (state): FavoritesListState => ({
      ...state,
      favoriteListArticlesStatus: LoadingStatus.Error,
      favoriteListArticles: [],
    }),
  ),
);

export const { selectAll: selectAllFavoritesLists } = favoritesEntityAdapter.getSelectors();
